import type { Config as VegaConfig } from "vega";
import type { Config } from "vega-lite";
import type { VizPalette } from "./tokens.js";

/** A clean, modern UI sans stack that matches the app shell. */
export const DEFAULT_FONT =
  "Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

export interface VizThemeOptions {
  /** Partially override the resolved palette (e.g. a custom categorical set). */
  palette?: Partial<VizPalette>;
  /** Font family applied to all text marks. */
  font?: string;
}

/** The palette with any caller overrides applied. */
function resolveThemePalette(
  palette: VizPalette,
  options?: VizThemeOptions,
): VizPalette {
  return { ...palette, ...options?.palette };
}

/**
 * The color ranges a chart's scales draw from — the one place a palette
 * becomes chart colors, shared by both grammars. `category` feeds nominal
 * series; `ramp`/`heatmap` (sequential) feed continuous color scales;
 * `diverging` feeds scales with a midpoint. Ranges the palette leaves unset
 * keep Vega's defaults.
 */
export function chartColorRanges(palette: VizPalette): {
  category: string[];
  ramp?: string[];
  heatmap?: string[];
  diverging?: string[];
} {
  return {
    category: palette.categorical,
    ...(palette.sequential?.length
      ? { ramp: palette.sequential, heatmap: palette.sequential }
      : {}),
    ...(palette.diverging?.length ? { diverging: palette.diverging } : {}),
  };
}

/**
 * Axis, legend and title styling. Written once for both grammars: these
 * config keys mean the same in Vega-Lite and Vega.
 */
function guideConfig(palette: VizPalette, font: string) {
  const { foreground, mutedForeground, border } = palette;
  return {
    axis: {
      labelColor: mutedForeground,
      labelFont: font,
      labelFontSize: 11,
      labelPadding: 6,
      // Long category labels ellipsize instead of pushing the plot around.
      labelLimit: 120,
      titleColor: foreground,
      titleFont: font,
      titleFontSize: 12,
      titleFontWeight: 600,
      titlePadding: 10,
      domainColor: border,
      domainWidth: 1,
      tickColor: border,
      tickSize: 5,
      gridColor: border,
      gridWidth: 1,
    },
    axisX: {
      grid: false,
      domain: true,
      ticks: true,
      labelAngle: 0,
    },
    axisY: {
      grid: true,
      domain: false,
      ticks: false,
      tickCount: 5,
    },
    legend: {
      labelColor: mutedForeground,
      labelFont: font,
      labelFontSize: 11,
      labelLimit: 120,
      titleColor: foreground,
      titleFont: font,
      titleFontSize: 12,
      titleFontWeight: 600,
      titleLimit: 160,
      symbolType: "circle",
      symbolSize: 80,
      // A legend taller than its grid cell would be clipped; past this many
      // entries Vega shows "…N entries" instead.
      symbolLimit: 12,
      orient: "right",
      offset: 16,
    },
    title: {
      color: foreground,
      font,
      fontSize: 15,
      fontWeight: 600,
      subtitleColor: mutedForeground,
      subtitleFont: font,
      subtitleFontSize: 12,
      anchor: "start",
      offset: 14,
    },
  } as const;
}

/**
 * Builds a Vega-Lite {@link Config} from a resolved palette. This is the heart
 * of "beautiful by default": muted hairline gridlines, no chart border box,
 * rounded bar tops, generous label spacing, and the design-system categorical
 * colors — all wired to the app's theme tokens.
 */
export function buildVegaConfig(
  palette: VizPalette,
  options?: VizThemeOptions,
): Config {
  const font = options?.font ?? DEFAULT_FONT;
  const resolved = resolveThemePalette(palette, options);
  const { background, foreground, border, primary, categorical } = resolved;
  const accent = categorical[0] ?? primary;

  return {
    background: "transparent",
    font,
    padding: 8,
    view: {
      stroke: null,
      continuousWidth: 320,
      continuousHeight: 280,
    },
    ...guideConfig(resolved, font),
    range: chartColorRanges(resolved),
    mark: { color: accent, font },
    bar: { cornerRadiusEnd: 4, color: accent },
    line: { strokeWidth: 2.5, color: accent },
    point: { filled: true, size: 70, color: accent },
    area: { line: true, opacity: 0.25, color: accent },
    arc: { stroke: background, strokeWidth: 1.5 },
    rule: { color: border },
    text: { color: foreground, font, fontSize: 11 },
    numberFormat: ",.0f",
  };
}

/**
 * The same theme as {@link buildVegaConfig}, as a Vega {@link VegaConfig} for
 * charts written in full Vega. Vega marks carry no default color of their
 * own, so the accent is set per mark type — a spec that sets `fill` wins.
 */
export function buildVegaRuntimeConfig(
  palette: VizPalette,
  options?: VizThemeOptions,
): VegaConfig {
  const font = options?.font ?? DEFAULT_FONT;
  const resolved = resolveThemePalette(palette, options);
  const { background, foreground, border, primary, categorical } = resolved;
  const accent = categorical[0] ?? primary;

  return {
    background: "transparent",
    padding: 8,
    ...guideConfig(resolved, font),
    range: chartColorRanges(resolved),
    group: { fill: null, stroke: null },
    rect: { fill: accent },
    area: { fill: accent },
    arc: { fill: accent, stroke: background, strokeWidth: 1.5 },
    symbol: { fill: accent, size: 64 },
    line: { stroke: accent, strokeWidth: 2.5 },
    rule: { stroke: border },
    path: { stroke: border },
    text: { fill: foreground, font, fontSize: 11 },
  };
}

/**
 * The single entry point from a palette to chart styling, for both grammars.
 * A palette source (e.g. an app-settings palette) plugs in here; nothing
 * else needs to know how colors reach Vega.
 */
export function buildChartTheme(
  palette: VizPalette,
  options?: VizThemeOptions,
): { vegaLite: Config; vega: VegaConfig } {
  return {
    vegaLite: buildVegaConfig(palette, options),
    vega: buildVegaRuntimeConfig(palette, options),
  };
}

type Plain = Record<string, unknown>;

function isPlainObject(value: unknown): value is Plain {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

/**
 * Deep-merges two Vega-Lite configs, with `override` winning. Used to let a
 * caller's `spec.config` refine the themed defaults without losing them.
 */
export function mergeConfig(base: Config, override?: Config): Config {
  if (!override) return base;
  const out: Plain = { ...(base as Plain) };
  for (const [key, value] of Object.entries(override as Plain)) {
    const existing = out[key];
    if (isPlainObject(existing) && isPlainObject(value)) {
      out[key] = mergeConfig(existing as Config, value as Config);
    } else if (value !== undefined) {
      out[key] = value;
    }
  }
  return out as Config;
}
